<template>
  <v-navigation-drawer
    :model-value="open"
    :class="SIDEBAR_CLASS"
    location="right"
    rail
    rail-width="0"
    permanent
    @transitionend="onTransitionEnd"
  >
    <div :class="{ 'd-flex flex-column align-center h-100' : !isPending }">
      <div class="d-flex align-center py-4 px-6 w-100">
        <template v-if="redactType">
          <app-button
            size="small"
            type="icon"
            @click="redactType = null"
          >
            <app-icon-ds icon="chevron-left" size="xs" />
          </app-button>
          <h2 class="text-grey-900 text--large ml-2">
            <template v-if="redactType === REDACT_TYPE_OPTIONS.ZONE">
              {{ $t('common.PdfRedactingSidebar.redactByZone') }}
            </template>
            <template v-else-if="redactType === REDACT_TYPE_OPTIONS.KEYWORDS">
              {{ $t('common.PdfRedactingSidebar.redactByKeywords') }}
            </template>
          </h2>
        </template>
        <h2 v-else class="text-grey-900 text--large">
          {{ $t('common.PdfRedactingSidebar.title') }}
        </h2>

        <v-menu
          class="rounded-lg"
          location="bottom left"
          transition="slide-y-transition"
        >
          <template v-slot:activator="{ props }">
            <app-button
              class="ml-auto mr-2"
              type="icon"
              size="small"
              v-bind="props"
            >
              <app-icon-ds
                icon="fa-question-circle"
                size="xs"
              />
            </app-button>
          </template>

          <v-list density="compact">
            <v-list-item :href="urlFaq" target="_blank">
              <v-list-item-title>
                {{ $t("common.app-bar.HelpMenuItem.helpCenter") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <app-button
          type="icon"
          size="small"
          @click="close"
        >
          <app-icon-ds
            icon="fa-xmark"
            size="xs"
            color="grey-600"
          />
        </app-button>
      </div>

      <v-divider class="w-100" />

      <v-skeleton-loader v-if="isPending" class="pa-6" type="card"></v-skeleton-loader>

      <div v-else class="d-flex flex-column pa-6 overflow-y-auto w-100">
        <template v-if="!redactType">
          <p class="text-grey-darken-3">{{ $t('common.PdfRedactingSidebar.tutorial') }}</p>

          <PdfRedactingSidebarType class="mt-4"
                                   :default-redact-type="redactType"
                                   @change-redact-type="redactType = $event">
          </PdfRedactingSidebarType>
        </template>

        <template v-else>
          <div v-if="redactType === REDACT_TYPE_OPTIONS.ZONE" class="redact-by-zone-container">
            <v-toolbar class="redact-by-zone-header" flat density="compact">
              <v-toolbar-title class="mx-4 my-3">
                <div class="d-flex align-center justify-space-between">
                  <h3 class="text-grey-900 text--small">
                    {{ $t('common.PdfRedactingSidebar.redactedZones', redactedZones.length) }}
                  </h3>
                  <AppButton
                    size="small"
                    type="outlined"
                    variant="neutral"
                    @click="isRedactByZoneComputed = !isRedactByZoneComputed"
                  >
                    {{ isRedactByZone ? $t('common.PdfRedactingSidebar.stop') : $t('common.PdfRedactingSidebar.start') }}
                  </AppButton>
                </div>
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text class="ma-4 pa-0">
              <div
                v-if="redactedZones.length === 0"
                class="d-flex flex-column justify-center align-center pa-5"
              >
                <v-img :src="getRedactByKeywordsIcon" width="32" />
                <div class="text-grey-800 text--small text-center mt-4">
                  <i18n-t keypath="common.PdfRedactingSidebar.redactByZoneDescription"
                          tag="span"
                          scope="global"
                  >
                    <template #start>
                      <span class="font-weight-medium">{{ $t('common.PdfRedactingSidebar.start') }}</span>
                    </template>
                    <template #stop>
                      <span class="font-weight-medium">{{ $t('common.PdfRedactingSidebar.stop') }}</span>
                    </template>
                  </i18n-t>
                </div>
              </div>
              <template v-else>
                <div
                  v-for="(rectangle) in redactedZones" :key="rectangle.id"
                  class="d-flex align-center justify-space-between mb-3"
                >
                  <p class="mb-0 text--small">
                    {{
                      $t('common.PdfRedactingSidebar.redactedZoneName',
                        { page: rectangle.page, count: rectangle.index },
                      )
                    }}
                  </p>
                  <DeletePopover
                    :item="rectangle"
                    @confirm="$emit('delete-rectangle', $event)"
                  />
                </div>
              </template>
            </v-card-text>
          </div>
        </template>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import DeletePopover from "@/common/popover/DeletePopover.vue"
import { REDACT_TYPE_OPTIONS } from '@/common/utils/redactTypes.ts'
import AppButton from '@/design-system/buttons/AppButton.vue'

import PdfRedactingSidebarType from "./PdfRedactingSidebarType.vue"

const SIDEBAR_CLASS = 'right-sidebar-container'

export default {
  name: "PdfRedactingSidebar",
  components: {
    AppButton,
    DeletePopover,
    PdfRedactingSidebarType,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
    /**
     * @model
     * A model to switch the redact by zone mode
     */
    isRedactByZone: {
      type: Boolean,
      default: false,
    },
    redactedZones: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'close',
    'update:isRedactByZone',
    'delete-rectangle',
    'fully-toggled',
  ],
  data () {
    return {
      redactType: null,
      REDACT_TYPE_OPTIONS,
      SIDEBAR_CLASS,
    }
  },
  computed: {
    urlFaq() {
      return `${import.meta.env.VUE_APP_KB_URL}/${this.$i18n.locale}`
    },
    getRedactByKeywordsIcon () {
      return new URL(`../../assets/img/redact-by-keywords.svg`, import.meta.url).href
    },
    isRedactByZoneComputed: {
      get () {
        return this.isRedactByZone
      },
      set (value) {
        this.$emit('update:isRedactByZone', value)
      },
    },
  },
  watch: {
    open (value) {
      if (!value) {
        this.redactType = null
        this.isRedactByZoneComputed = false
      }
    },
  },
  methods: {
    close () {
      this.$emit('close')
    },
    onTransitionEnd (e) {
      if (e.target.classList.contains(SIDEBAR_CLASS)) {
        this.$emit('fully-toggled')
      }
    },
  },
}
</script>

<style scoped lang="scss">
  .right-sidebar-container {
    .redact-by-zone-container {
      border-radius: 8px;
      border: 1px solid rgb(var(--v-theme-grey-100));
    }
    .redact-by-zone-header {
      border-radius: 8px 8px 0 0;
      background-color: rgb(var(--v-theme-grey-50));
    }
  }
</style>
